// src/features/executive/executive-nav.utils.ts
// Pure routing/permission helpers shared by ExecutiveLayout, ExecutiveIndex,
// ExecutiveTabsNav and OverviewTab so the tab → path → permission mapping
// lives in exactly one place.
import { routeName } from 'routes/routes-name'
import type { ExecutiveTab, UserExecutivePermissions } from 'api/executive/executive.types'

type ExecutiveTabs = UserExecutivePermissions['executiveDashboardTabs']

export type OverviewSectionKey = Exclude<ExecutiveTab, 'overview'>

export const EXECUTIVE_TAB_ORDER: ExecutiveTab[] = ['overview', 'performance', 'account', 'sales', 'insight']

export const EXECUTIVE_TAB_PATHS: Record<ExecutiveTab, string> = {
  overview: routeName.executiveOverview,
  performance: routeName.executivePerformance,
  account: routeName.executiveAccount,
  sales: routeName.executiveSales,
  insight: routeName.executiveInsight,
}

// Permission key per tab. `overview` is null: it is open to anyone who can
// open at least one real tab.
const PERMISSION_KEY: Record<ExecutiveTab, keyof ExecutiveTabs | null> = {
  overview: null,
  performance: 'performance',
  account: 'account',
  sales: 'sales',
  insight: 'consumerInsight',
}

export function resolveExecutiveTab(pathname: string): ExecutiveTab {
  if (pathname.startsWith(routeName.executivePerformance)) return 'performance'
  if (pathname.startsWith(routeName.executiveAccount)) return 'account'
  if (pathname.startsWith(routeName.executiveSales)) return 'sales'
  if (pathname.startsWith(routeName.executiveInsight)) return 'insight'
  return 'overview'
}

export function isExecutiveTabAllowed(tab: ExecutiveTab, tabs: ExecutiveTabs): boolean {
  const key = PERMISSION_KEY[tab]
  if (key) return Boolean(tabs[key])
  return EXECUTIVE_TAB_ORDER.some((t) => t !== 'overview' && isExecutiveTabAllowed(t, tabs))
}

/** Where `/executive` (and a denied tab) should land. */
export function getExecutiveEntryPath(tabs: ExecutiveTabs): string {
  return isExecutiveTabAllowed('overview', tabs) ? EXECUTIVE_TAB_PATHS.overview : routeName.dashboard
}

/** Overview sections in tab order, limited to tabs the user may open. */
export function getOverviewSections(tabs: ExecutiveTabs): OverviewSectionKey[] {
  return EXECUTIVE_TAB_ORDER
    .filter((t): t is OverviewSectionKey => t !== 'overview')
    .filter((t) => isExecutiveTabAllowed(t, tabs))
}
